import React, { useState, useEffect, useRef } from 'react';
import Icon from '@mdi/react';
import { mdiChevronDown, mdiChevronRight, mdiInformationOutline } from '@mdi/js';
import './ClassesHelpModal.css';

const FinancesHelpModal = ({ isOpen, onClose }) => {
    const [expandedSections, setExpandedSections] = useState([]);
    const sectionRefs = useRef({});

    useEffect(() => {
        if (!isOpen) setExpandedSections([]);
    }, [isOpen]);

    const toggleSection = (sectionId) => {
        const isExpanding = !expandedSections.includes(sectionId);
        setExpandedSections(prev =>
            prev.includes(sectionId)
                ? prev.filter(id => id !== sectionId)
                : [...prev, sectionId]
        );
        if (isExpanding) {
            setTimeout(() => {
                sectionRefs.current[sectionId]?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
            }, 0);
        }
    };

    if (!isOpen) return null;

    const sections = [
        {
            id: 'stripe-connect',
            title: '1. Connecting your Stripe account',
            content: (
                <>
                    <p>Dance Localé uses <strong>Stripe Connect</strong> to process payments on your behalf. To accept online payments for classes, private lessons, or event tickets, go to <strong>Manage Finances</strong> and use the <strong>Set up payouts</strong> card. You will be taken through Stripe's onboarding to create or link an existing account.</p>
                    <p>If you do not connect Stripe, students can still be manually marked as paid, but the platform cannot collect online payments on your behalf.</p>
                </>
            )
        },
        {
            id: 'payout-country',
            title: '2. Choosing your payout country',
            content: (
                <>
                    <p>When you first set up payouts, you choose the <strong>country where your bank account is located</strong>. This is shown on the <strong>Set up payouts</strong> card in <strong>Manage Finances</strong>, just before you connect. The available countries are the ones that can receive payouts in your school's currency, so if your currency is NZD your payout country is New Zealand.</p>
                    <p>Pick this carefully. Your payout country is set on your Stripe account when it is created and <strong>cannot be changed afterwards</strong> (this is a Stripe rule, not a Dance Localé one). Once your account exists, the country option no longer appears.</p>
                    <p><strong>If you chose the wrong country</strong> and need a different one, the only way to change it is to <strong>remove your current payout account and connect a new one</strong>:</p>
                    <ul>
                        <li>Open your Stripe account from the <strong>Manage Payout Account</strong> button and close or deactivate it in your Stripe dashboard, or contact us to disconnect it.</li>
                        <li>Once disconnected, the <strong>Set up payouts</strong> card returns to its starting state, and you can connect again and choose the correct country.</li>
                    </ul>
                    <p>Any funds already paid out are unaffected. If you have an upcoming balance, make sure it has paid out before disconnecting.</p>
                </>
            )
        },
        {
            id: 'platform-fee',
            title: '3. Platform fee',
            content: (
                <>
                    <p>Students pay one combined <strong>Service fee</strong> at checkout. It covers the Dance Localé platform fee (2.5% of the lesson, class or ticket price) and payment processing through Stripe. It is not a separate Stripe charge. The fee comes on top of the price you set, so you receive your full amount. There are no monthly subscription fees.</p>
                    <p>Students see the service fee and the total before they pay, on every checkout. Processing cost varies with where the card is from. Travel periods abroad are processed at international card rates, which is covered in the student's service fee. Your payout does not change.</p>
                </>
            )
        },
        {
            id: 'dashboard',
            title: '4. Reading the Financial Dashboard',
            content: (
                <>
                    <p>The Financial Dashboard gives you an overview of all payment activity across classes, private lessons, and events. From here you can:</p>
                    <ul>
                        <li>View a revenue summary by period</li>
                        <li>See individual transaction history across all payment types</li>
                        <li>Track payout history and upcoming scheduled transfers to your bank</li>
                        <li>Monitor outstanding payments and unpaid enrolments</li>
                    </ul>
                </>
            )
        },
        {
            id: 'payouts',
            title: '5. Payouts',
            content: (
                <>
                    <p>Funds collected through Dance Localé are held in your Stripe connected account and paid out automatically to your nominated bank account on a weekly or monthly schedule, depending on your Stripe account settings.</p>
                    <p>Payout timing is controlled by Stripe and depends on your account's payout schedule and any pending verification requirements. Check your Stripe dashboard for payout history and upcoming transfer dates.</p>
                </>
            )
        },
        {
            id: 'refunds',
            title: '6. Refunds',
            content: (
                <>
                    <p>Refunds can be issued from several places depending on the context:</p>
                    <ul>
                        <li><strong>Class enrolment removal</strong> — Issue a refund when removing a student from a class. You can enter a custom amount or refund the full payment.</li>
                        <li><strong>Session or class cancellation</strong> — When a session is cancelled, you can refund affected students. A custom amount can be entered for partial refunds.</li>
                        <li><strong>Event cancellation</strong> — Cancelling a ticketed event triggers a bulk refund to all paid purchasers automatically.</li>
                    </ul>
                    <p>Refunds are processed through Stripe and typically appear in the customer's account within 5–10 business days.</p>
                </>
            )
        },
        {
            id: 'manual-payments',
            title: '7. Manual (offline) payments',
            content: (
                <>
                    <p>If a student pays by cash or another offline method, mark their enrolment or booking as paid manually. Open the student's record and use the <strong>Mark as Paid</strong> option. This updates payment status without processing a Stripe transaction.</p>
                    <p>Manually marked payments are recorded in the Financial Dashboard alongside online payments for a complete view of your income.</p>
                </>
            )
        },
        {
            id: 'request-payment-booking',
            title: '8. Booking — Request Payment (resend or override)',
            content: (
                <>
                    <p>For confirmed unpaid bookings (after-lesson payment), open the booking detail and click <strong>Request Payment</strong>. You can:</p>
                    <ul>
                        <li><strong>Show QR</strong> — generates a QR code for in-person scanning (best with Apple Pay / Google Pay).</li>
                        <li><strong>Send Email</strong> — emails the student an updated payment link.</li>
                        <li><strong>Mark paid</strong> — appears when bank transfer is enabled; flags the booking paid offline.</li>
                    </ul>
                    <p>For before-lesson bookings that stay unpaid, the system sends this same payment email once automatically, about 2 hours after booking.</p>
                    <p>Tick <strong>Override lesson fee</strong> to charge a different amount on this link (for example, a one-off discount). Only the lesson fee changes, venue hire stays as the booking's selected venue rate, and the platform + Stripe processing fees recalculate automatically.</p>
                    <p>Each new QR or email <strong>invalidates the previous link</strong> for that booking, only the most recent payment link is valid. The replacement email includes a note so the student knows to ignore any earlier link.</p>
                </>
            )
        }
    ];

    return (
        <div className="classes-help-overlay" onClick={onClose}>
            <div className="classes-help-modal" onClick={e => e.stopPropagation()}>
                <div className="classes-help-header">
                    <div className="classes-help-header-content">
                        <Icon path={mdiInformationOutline} size={1.2} color="var(--dl-color-purple-default)" />
                        <h2>Finances &amp; Payments Help</h2>
                    </div>
                    <button className="classes-help-close" onClick={onClose}>×</button>
                </div>

                <div className="classes-help-body">
                    {sections.map(section => (
                        <div key={section.id} className="help-accordion-section" ref={el => sectionRefs.current[section.id] = el}>
                            <button
                                className="help-accordion-header"
                                onClick={() => toggleSection(section.id)}
                            >
                                <Icon
                                    path={expandedSections.includes(section.id) ? mdiChevronDown : mdiChevronRight}
                                    size={0.8}
                                    color="var(--dl-color-purple-default)"
                                />
                                <span>{section.title}</span>
                            </button>
                            {expandedSections.includes(section.id) && (
                                <div className="help-accordion-content">
                                    {section.content}
                                </div>
                            )}
                        </div>
                    ))}
                </div>

                <div className="classes-help-footer">
                    <button className="classes-help-btn-secondary" onClick={onClose}>Got it!</button>
                </div>
            </div>
        </div>
    );
};

export default FinancesHelpModal;
